import { Avatar, Button, Dialog, DialogContent, TextField, Typography } from '@mui/material';
import { useMeCore } from '../../core/modules/me';
import { useGlobalCore } from '../../core/modules/global';
import { ME_USER_DIALOG } from '../../core/constants/dialog';
import Spinner from './Spinner';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';

interface FormData {
  name: string;
}

const schema = yup.object().shape({
  name: yup.string().required('You must enter a name'),
});

const defaultValues: FormData = {
  name: '',
};

function UserDialog() {
  const { t } = useTranslation();
  const { dialogs, closeDialog } = useGlobalCore();
  const { user, putMeUser, getMeUser } = useMeCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  useEffect(() => {
    if (user) {
      setValue('name', user.name);
    }
  }, [user, setValue]);

  const onSubmit = useCallback(
    async (values: FormData) => {
      setLoading(true);
      putMeUser(
        values,
        () => {
          getMeUser();
          setLoading(false);
          closeDialog(ME_USER_DIALOG);
        },
        () => {
          setLoading(false);
        }
      );
    },
    [putMeUser, getMeUser, closeDialog]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={() => {
        closeDialog(ME_USER_DIALOG);
      }}
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(ME_USER_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Perfil
              </Typography>
            </div>

            <div className="flex flex-col justify-center items-center mb-32">
              <Avatar sx={{ width: 100, height: 100 }}>
                {user?.name[0]}
              </Avatar>
            </div>

            <form name="infoForm" noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="flex items-center w-full">
                <div className="ms-10 flex-1">
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label={t('label.name')}
                        id="name"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        disabled={loading}
                        required
                        fullWidth
                      />
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-center mt-32">
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ borderRadius: 50 }}
                  disabled={!isEmpty(errors) || loading}
                  type="submit"
                >
                  <Spinner show={loading} />
                  {t('button.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UserDialog;
